import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { getProfile } from '../../lib/api';
import type { Profile } from '../../lib/types';
import { notify, safeActionError } from '../../feedback/notifications';
import { sessionEvents } from './sessionEvents';
import { SESSION_EXPIRED_MESSAGE, SESSION_UNAVAILABLE_MESSAGE, SessionCheckTimeout, sessionDeadline, sessionErrorKind, type SessionState } from './sessionPolicy';
interface AuthValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  profileError: string | null;
  loading: boolean;
  sessionState: SessionState;
  sessionError: string | null;
  retrySession: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}
const AuthContext = createContext<AuthValue | null>(null);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null),
    [profile, setProfile] = useState<Profile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null),
    [loading, setLoading] = useState(true);
  const [sessionState, setSessionState] = useState<SessionState>('restoring'),
    [sessionError, setSessionError] = useState<string | null>(null);
  const alive = useRef(false),
    version = useRef(0),
    profileRequest = useRef(0);
  const activeUserId = useRef<string | null>(null),
    currentSession = useRef<Session | null>(null),
    currentProfile = useRef<Profile | null>(null);
  const currentState = useRef<SessionState>('restoring'),
    explicitSignOut = useRef(false),
    renewalRejected = useRef(false);
  const pending = useRef<{
    version: number;
    work: Promise<void>;
  } | null>(null);
  const status = useCallback((state: SessionState, message: string | null = null) => {
    currentState.current = state;
    setSessionState(state);
    setSessionError(message);
  }, []);
  const expire = useCallback(() => {
    if (!alive.current) return;
    renewalRejected.current = false;
    version.current++;
    profileRequest.current++;
    currentSession.current = null;
    currentProfile.current = null;
    activeUserId.current = null;
    notify.clear();
    queryClient.clear();
    setSession(null);
    setProfile(null);
    setProfileError(null);
    setLoading(false);
    status('expired', SESSION_EXPIRED_MESSAGE);
  }, [queryClient, status]);
  const unavailable = useCallback(() => {
    if (alive.current) {
      setLoading(false);
      status('unavailable', SESSION_UNAVAILABLE_MESSAGE);
    }
  }, [status]);
  const loadProfile = useCallback(async (next: Session) => {
    const request = ++profileRequest.current;
    setProfileError(null);
    if (!currentProfile.current) setLoading(true);
    try {
      const value = await sessionDeadline(Promise.resolve().then(() => getProfile(next.user.id)));
      if (!alive.current || request !== profileRequest.current || activeUserId.current !== next.user.id) return;
      currentProfile.current = value;
      setProfile(value);
    } catch (error) {
      if (alive.current && request === profileRequest.current) setProfileError(error instanceof SessionCheckTimeout ? 'Your account profile took too long to load. Check your connection and retry.' : 'We could not load your account profile. ' + safeActionError(error));
    } finally {
      if (alive.current && request === profileRequest.current) setLoading(false);
    }
  }, []);
  const apply = useCallback((next: Session | null) => {
    if (!alive.current) return;
    const id = next?.user.id ?? null;
    if (id !== activeUserId.current) {
      notify.clear();
      queryClient.clear();
      profileRequest.current++;
      currentProfile.current = null;
      setProfile(null);
      setProfileError(null);
      activeUserId.current = id;
    }
    if (next && currentSession.current && next.access_token !== currentSession.current.access_token) renewalRejected.current = false;
    currentSession.current = next;
    setSession(next);
    if (!next) {
      setLoading(false);
      if (currentState.current !== 'expired') status('anonymous');
      return;
    }
    status('authenticated', renewalRejected.current ? 'Automatic session renewal failed. The current session is still valid; sign in again before it expires.' : null);
    if (currentProfile.current?.user_id === id) {
      setLoading(false);
      return;
    }
    void loadProfile(next);
  }, [loadProfile, queryClient, status]);
  const observe = useCallback(async (work: Promise<void>, revision: number) => {
    try {
      await sessionDeadline(work);
    } catch {
      if (alive.current && version.current === revision) unavailable();
    }
  }, [unavailable]);
  const checkSession = useCallback((refresh = false): Promise<void> => {
    if (pending.current && pending.current.version === version.current) return observe(pending.current.work, pending.current.version);
    const revision = ++version.current;
    if (refresh) {
      setLoading(false);
      status('unavailable', 'Checking your session. Your draft is preserved; wait before submitting again.');
    }
    const work = Promise.resolve().then(async () => {
      const result = refresh ? await supabase.auth.refreshSession() : await supabase.auth.getSession();
      if (refresh && result.error && sessionErrorKind(result.error) === 'terminal') {
        const stored = await supabase.auth.getSession();
        if (!stored.error && stored.data.session) return {
          data: {
            session: stored.data.session
          },
          error: null
        };
      }
      return {
        data: {
          session: result.data.session
        },
        error: result.error
      };
    }).then(({
      data,
      error
    }) => {
      if (!alive.current || version.current !== revision) return;
      if (error) {
        if (sessionErrorKind(error) === 'terminal') expire();else unavailable();
        return;
      }
      if (!data.session && currentSession.current) {
        expire();
        return;
      }
      apply(data.session);
    }).catch(error => {
      if (alive.current && version.current === revision) {
        if (sessionErrorKind(error) === 'terminal') expire();else unavailable();
      }
    });
    const operation = {
      version: revision,
      work
    };
    pending.current = operation;
    void work.finally(() => {
      if (pending.current === operation) pending.current = null;
    });
    return observe(work, revision);
  }, [apply, expire, observe, status, unavailable]);
  useEffect(() => {
    alive.current = true;
    let effectActive = true,
      recoveryScheduled = false,
      lastRecovery: number | null = null;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (operation: () => void) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        if (effectActive && alive.current) operation();
      }, 0);
      timers.add(timer);
    };
    // Register first. Auth callbacks stay synchronous; SDK calls run outside them.
    const {
      data
    } = supabase.auth.onAuthStateChange((event, next) => {
      if (!effectActive || !alive.current) return;
      if (event === 'INITIAL_SESSION' && !next) return;
      sessionEvents.changed();
      const revision = ++version.current;
      if (event === 'SIGNED_OUT') {
        if (renewalRejected.current && !explicitSignOut.current) {
          expire();
          return;
        }
        const hadUser = Boolean(activeUserId.current);
        if (explicitSignOut.current) status('anonymous');
        apply(null);
        if (hadUser && !explicitSignOut.current && currentState.current !== 'expired') status('anonymous', 'You have been signed out. Please sign in again to continue.');
        return;
      }
      if (event === 'TOKEN_REFRESHED') renewalRejected.current = false;
      later(() => {
        if (version.current === revision) apply(next);
      });
    });
    const unsubscribe = sessionEvents.subscribe(failure => {
      if (!effectActive || !alive.current || explicitSignOut.current) return;
      if (failure.kind === 'terminal') {
        renewalRejected.current = true;
        later(() => void checkSession());
        return;
      }
      if (failure.kind === 'transient' || failure.kind === 'configuration') {
        unavailable();
        return;
      }
      if (recoveryScheduled || pending.current && pending.current.version === version.current) return;
      if (lastRecovery !== null && Date.now() - lastRecovery < 30_000) {
        unavailable();
        return;
      }
      lastRecovery = Date.now();
      recoveryScheduled = true;
      later(() => {
        recoveryScheduled = false;
        void checkSession(true).then(() => {
          if (alive.current && currentState.current === 'authenticated') void queryClient.invalidateQueries({
            refetchType: 'active'
          });
        });
      });
    });
    void checkSession();
    return () => {
      effectActive = false;
      alive.current = false;
      version.current++;
      profileRequest.current++;
      pending.current = null;
      for (const timer of timers) clearTimeout(timer);
      unsubscribe();
      data.subscription.unsubscribe();
    };
  }, [apply, checkSession, expire, queryClient, status, unavailable]);
  const refreshProfile = useCallback(async () => {
    await checkSession();
    const next = currentSession.current;
    if (next && currentState.current === 'authenticated') await loadProfile(next);
  }, [checkSession, loadProfile]);
  const signOut = useCallback(async () => {
    explicitSignOut.current = true;
    try {
      const {
        error
      } = await supabase.auth.signOut();
      if (error) throw error;
    } finally {
      explicitSignOut.current = false;
    }
  }, []);
  const value = useMemo<AuthValue>(() => ({
    session,
    user: session?.user ?? null,
    profile,
    profileError,
    loading,
    sessionState,
    sessionError,
    retrySession: checkSession,
    refreshProfile,
    signOut
  }), [session, profile, profileError, loading, sessionState, sessionError, checkSession, refreshProfile, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
